/**
 * NodeDetailPane (S1.1 · S5.2a) — right pane of the taxonomy admin page.
 *
 * Core fields stay in view: code (read-only immutable business code) + the two
 * bilingual names (44px inputs) + level chip + breadcrumb. The secondary
 * settings that already round-trip on the real backend — sort order, NetSuite
 * segment ref, and status/active visibility — live behind a "More details /
 * 更多設定" disclosure (S5.2a progressive disclosure). The MANDATORY change
 * comment stays visible and enforced on save of ANY change, and the active
 * Switch keeps its deactivate-with-replacement flow. No hard delete. Inactive
 * nodes show a read-only status tag instead of the Switch (the mock API has no
 * reactivate endpoint yet — affordance is hidden, not disabled).
 *
 * Speculative enrich fields (description/iconKey/synonyms/externalMappings) are
 * intentionally NOT wired here — that is the OQ-4-gated S5.2b; wiring an input
 * that round-trips in MSW but not prod would silently drop user data.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  App,
  Button,
  Card,
  Collapse,
  InputNumber,
  Input,
  Switch,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import StatusTag from '@/components/common/StatusTag';
import PurpleTag from '@/components/common/PurpleTag';
import DeactivateNodeModal from './DeactivateNodeModal';
import { useFormStyles } from '@/styles/formStyles';
import { useElevation } from '@/styles';
import { controlHeightTokens } from '@/styles/tokens';
import { buildBreadcrumb, extractGuardError, useTaxonomyMutations } from '@/hooks/useTaxonomy';
import type { CategoryLevelConfig, ProductCategory } from '@/types/api/productCategory';

const { Text, Title } = Typography;

const nodeDetailSchema = z.object({
  nameEn: z.string().trim().min(1, 'taxonomy:validation.nameEnRequired'),
  nameZh: z.string().trim().min(1, 'taxonomy:validation.nameZhRequired'),
  sortOrder: z.number().min(0),
  netsuiteSegmentValueId: z.string().trim().optional(),
  comment: z.string().trim().min(1, 'taxonomy:validation.commentRequired'),
});

type NodeDetailFormData = z.infer<typeof nodeDetailSchema>;

export interface NodeDetailPaneProps {
  node: ProductCategory | null;
  nodes: ProductCategory[];
  levels: CategoryLevelConfig[];
  /** Called after any successful mutation (page refreshes the tree). */
  onChanged: () => void;
}

const NodeDetailPane = ({ node, nodes, levels, onChanged }: NodeDetailPaneProps) => {
  const { t, i18n } = useTranslation(['taxonomy', 'common']);
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const formStyles = useFormStyles();
  const elevation = useElevation();
  const { updateNode } = useTaxonomyMutations();
  const isZh = i18n.language.startsWith('zh');

  const [deactivating, setDeactivating] = useState<ProductCategory | null>(null);

  const formValues = useMemo<NodeDetailFormData | undefined>(
    () =>
      node
        ? {
            nameEn: node.nameEn,
            nameZh: node.nameZh,
            sortOrder: node.sortOrder,
            netsuiteSegmentValueId: node.netsuiteSegmentValueId ?? '',
            comment: '',
          }
        : undefined,
    [node]
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, touchedFields, isSubmitting },
  } = useForm<NodeDetailFormData>({
    resolver: zodResolver(nodeDetailSchema),
    defaultValues: {
      nameEn: '',
      nameZh: '',
      sortOrder: 0,
      netsuiteSegmentValueId: '',
      comment: '',
    },
    values: formValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (formValues) reset(formValues);
  }, [formValues, reset]);

  const levelConfig = node ? levels.find((l) => l.level === node.depth) : undefined;
  const levelLabel = levelConfig
    ? isZh
      ? levelConfig.labelZh
      : levelConfig.labelEn
    : node
      ? `L${node.depth}`
      : '';

  const breadcrumb = node ? buildBreadcrumb(nodes, node.id, isZh ? 'zh' : 'en') : '';

  const submit = async (data: NodeDetailFormData) => {
    if (!node) return;
    try {
      await updateNode(node.id, {
        nameEn: data.nameEn.trim(),
        nameZh: data.nameZh.trim(),
        sortOrder: data.sortOrder,
        netsuiteSegmentValueId: data.netsuiteSegmentValueId?.trim() || undefined,
        comment: data.comment.trim(),
      });
      message.success(t('taxonomy:detail.savedSuccess'));
      onChanged();
    } catch (error) {
      const guard = extractGuardError(error);
      message.error(guard ? t(`taxonomy:errors.${guard}`) : t('common:messages.updateFailed'));
    }
  };

  const fieldError = (name: keyof NodeDetailFormData) =>
    errors[name] && (touchedFields[name] || name === 'comment') ? (
      <Text style={formStyles.errorStyle}>{t(errors[name]?.message ?? '')}</Text>
    ) : null;

  if (!node) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16 tw-text-center">
        <Title level={4} style={{ color: token.colorTextSecondary, marginBottom: token.marginXS }}>
          {t('taxonomy:detail.emptyTitle')}
        </Title>
        <Text type="secondary">{t('taxonomy:detail.emptyHint')}</Text>
      </div>
    );
  }

  const levelHidden = levelConfig ? !levelConfig.enabled : false;
  const requiredMark = <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>;

  return (
    <div>
      {/* Header: name + level chip. Status/active + secondary settings moved
          into the "More details" disclosure below (S5.2a progressive disclosure). */}
      <div className="tw-mb-2">
        <Title level={4} style={{ marginBottom: token.marginXXS }}>
          {isZh ? node.nameZh : node.nameEn}
          <Text
            type="secondary"
            style={{ fontSize: token.fontSize, fontWeight: 'normal', marginLeft: token.marginSM }}
          >
            {isZh ? node.nameEn : node.nameZh}
          </Text>
        </Title>
        <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
          <PurpleTag>{levelLabel}</PurpleTag>
          {levelHidden && (
            <Tooltip title={t('taxonomy:tree.hiddenLevelTooltip')}>
              <Tag bordered={false}>{t('taxonomy:tree.hiddenLevel')}</Tag>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Breadcrumb along the materialized path */}
      <Text type="secondary" style={{ display: 'block', marginBottom: token.marginLG }}>
        {breadcrumb}
      </Text>

      <form onSubmit={handleSubmit(submit)}>
        <div style={formStyles.fieldContainerStyle}>
          {/* Code — immutable, read-only */}
          <Text style={formStyles.labelStyle}>{t('taxonomy:fields.code')}</Text>
          <div className="tw-flex tw-items-baseline tw-gap-2" style={{ marginBottom: '16px' }}>
            <Text strong style={{ fontSize: token.fontSizeLG }}>
              {node.code}
            </Text>
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {t('taxonomy:detail.codeImmutable')}
            </Text>
          </div>

          <Text style={formStyles.labelStyle}>
            {requiredMark}
            {t('taxonomy:fields.nameEn')}
          </Text>
          <Controller
            name="nameEn"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                size="large"
                style={formStyles.inputStyle}
                status={errors.nameEn && touchedFields.nameEn ? 'error' : undefined}
              />
            )}
          />
          {fieldError('nameEn')}

          <Text style={{ ...formStyles.labelStyle, marginTop: '16px' }}>
            {requiredMark}
            {t('taxonomy:fields.nameZh')}
          </Text>
          <Controller
            name="nameZh"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                size="large"
                style={formStyles.inputStyle}
                status={errors.nameZh && touchedFields.nameZh ? 'error' : undefined}
              />
            )}
          />
          {fieldError('nameZh')}

          {/* More details — progressive disclosure of the secondary settings
              that already round-trip on the real backend: sort order, NetSuite
              segment ref, and status/active visibility. Card container carries
              the Wave-2 shadow-border elevation (useElevation().card ⇒
              radiusTokens.card). forceRender keeps the fields registered so a
              collapsed panel still submits their values. */}
          <Card
            style={{ ...elevation.card, marginTop: '16px' }}
            styles={{ body: { padding: token.paddingSM } }}
          >
            <Collapse
              ghost
              items={[
                {
                  key: 'more',
                  forceRender: true,
                  label: <Text strong>{t('taxonomy:detail.moreDetails')}</Text>,
                  children: (
                    <div>
                      <Text style={formStyles.labelStyle}>{t('taxonomy:fields.sortOrder')}</Text>
                      <Controller
                        name="sortOrder"
                        control={control}
                        render={({ field }) => (
                          <InputNumber
                            value={field.value}
                            onChange={(value) => field.onChange(value ?? 0)}
                            onBlur={field.onBlur}
                            size="large"
                            min={0}
                            style={formStyles.inputStyle}
                          />
                        )}
                      />

                      <Text style={{ ...formStyles.labelStyle, marginTop: '16px' }}>
                        {t('taxonomy:fields.netsuiteRef')}
                      </Text>
                      <Controller
                        name="netsuiteSegmentValueId"
                        control={control}
                        render={({ field }) => (
                          <Input
                            {...field}
                            size="large"
                            placeholder={t('taxonomy:detail.netsuitePlaceholder')}
                            style={formStyles.inputStyle}
                          />
                        )}
                      />

                      {/* Status / active visibility. Deactivate keeps its
                          replacement flow (DeactivateNodeModal); an inactive node
                          shows a read-only tag because there is no reactivate
                          endpoint yet (hide-don't-disable). */}
                      <div
                        className="tw-flex tw-items-center tw-gap-2"
                        style={{ marginTop: '16px' }}
                      >
                        {node.isActive ? (
                          <>
                            <Text type="secondary">{t('taxonomy:fields.active')}</Text>
                            <Switch
                              checked
                              onChange={(checked) => {
                                if (!checked) setDeactivating(node);
                              }}
                            />
                          </>
                        ) : (
                          <StatusTag status="inactive" label={t('common:status.inactive')} />
                        )}
                      </div>
                    </div>
                  ),
                },
              ]}
            />
          </Card>

          <Text style={{ ...formStyles.labelStyle, marginTop: '16px' }}>
            {requiredMark}
            {t('taxonomy:fields.comment')}
          </Text>
          <Controller
            name="comment"
            control={control}
            render={({ field }) => (
              <Input.TextArea
                {...field}
                rows={2}
                placeholder={t('taxonomy:detail.commentPlaceholder')}
                style={{ ...formStyles.inputStyle, height: 'auto' }}
                status={errors.comment ? 'error' : undefined}
              />
            )}
          />
          {/* The change comment is MANDATORY — surface its error even when the
              field is still untouched (e.g. the user edits a name and hits Save
              without touching the comment), matching the fieldError helper's
              always-show intent for the comment field. Without this, a blocked
              submit gives no visible reason. */}
          {fieldError('comment')}
        </div>

        <div className="tw-flex tw-justify-end">
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            loading={isSubmitting}
            style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
          >
            {t('taxonomy:detail.save')}
          </Button>
        </div>
      </form>

      <DeactivateNodeModal
        node={deactivating}
        nodes={nodes}
        onClose={() => setDeactivating(null)}
        onDeactivated={onChanged}
      />
    </div>
  );
};

export default NodeDetailPane;
